import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '@fuxi/eevee-icon';
import dayjs from 'dayjs';
import { useLatest } from 'ahooks';
import { Toast, When } from '@/components';
import {
  setCurrentStep,
  setFormValue,
  setNextBtnConfig,
  setPrevBtnConfig,
  useAIDiagnoseStore,
} from '../../store/useAIDiagnoseStore';
import { THERAPY_TYPE_2_TEXT } from '../../utils';
import { BirthdaySelector } from '../birthday-selector';
import { ShowTitle } from '../show-title';
import weightlossImg from './images/weightloss.png';
import weightlossSelectImg from './images/weightloss-select.png';
import bodyAdjustImg from './images/body-adjust.png';
import bodyAdjustSelectImg from './images/body-adjust-select.png';
import painReliefImg from './images/pain-relief.png';
import painReliefSelectImg from './images/pain-relief-select.png';
import physicalImg from './images/physical.png';
import physicalSelectImg from './images/physical-select.png';

import cx from './index.module.scss';

const THERAPY_TYPE_2_IMAGE: Record<AIDiagnose.TherapyType, { default: string; selected: string }> = {
  bodyAdjust: {
    default: bodyAdjustImg,
    selected: bodyAdjustSelectImg,
  },
  painRelief: {
    default: painReliefImg,
    selected: painReliefSelectImg,
  },
  physicalImprove: {
    default: physicalImg,
    selected: physicalSelectImg,
  },
  weightLoss: {
    default: weightlossImg,
    selected: weightlossSelectImg,
  },
};

interface Props {}

const THERAPY_TYPE_2_TIME: Record<AIDiagnose.TherapyType, number> = {
  weightLoss: 3,
  painRelief: 1,
  physicalImprove: 2,
  bodyAdjust: 1,
};

export const HomeTherapyRequests: React.FC<Props> = () => {
  const [isBirthdaySelectorShow, setIsBirthdaySelectorShow] = useState(false);
  const { selectTherapyType, userInfo } = useAIDiagnoseStore.use.formValue();
  const currentStep = useAIDiagnoseStore.use.currentStep();
  const latestBirthday = useLatest(userInfo?.birthday);

  const allProcessSpendTime = useMemo(
    () =>
      selectTherapyType?.reduce((accur, cur) => {
        accur += THERAPY_TYPE_2_TIME[cur];

        return accur;
      }, 0),
    [selectTherapyType]
  );

  const handleClickItem = useCallback(
    (type: AIDiagnose.TherapyType) => () => {
      if (selectTherapyType.includes(type)) {
        setFormValue({ selectTherapyType: selectTherapyType?.filter(item => item !== type) });
      } else {
        setFormValue({ selectTherapyType: [...selectTherapyType, type] });
      }
    },
    [selectTherapyType]
  );

  // 根据步骤和状态控制按钮显示配置
  useEffect(() => {
    if (currentStep === -2) {
      setPrevBtnConfig({ show: false });
      setNextBtnConfig({
        text: '下一步',
        disable: !selectTherapyType?.length,
        action: () => {
          setCurrentStep(prev => ({ currentStep: prev + 1 }));
        },
      });
    } else if (currentStep === -1) {
      setPrevBtnConfig({
        show: true,
        action: () => {
          setCurrentStep(prev => ({ currentStep: prev - 1 }));
        },
      });
      setNextBtnConfig({
        text: '下一步',
        disable: !(userInfo?.birthday && userInfo?.gender),
        action: () => {
          const today = dayjs();
          const age = today.diff(latestBirthday.current, 'year');

          if (age < 15 || age > 100) {
            Toast.showVScreen('请选择正确的出生日期');
          } else {
            setCurrentStep(prev => ({ currentStep: prev + 1 }));
          }
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, selectTherapyType, userInfo]);

  return (
    <div className={cx('HomeTherapyRequests')}>
      <ShowTitle
        title={currentStep === -2 ? '告诉我您的理疗诉求' : '看看您的个人信息是否正确'}
        subTitle={currentStep === -2 ? '请选择您最期望改善的方向，可以进行多选' : ''}
      />
      <div className={cx('wrapper')}>
        {currentStep === -2 ? (
          <div className={cx('select-therapy-type')}>
            {Object.keys(THERAPY_TYPE_2_TEXT).map((key: any) => (
              <div
                key={key}
                className={cx('therapy-item', { select: selectTherapyType.includes(key) })}
                onClick={handleClickItem(key)}
              >
                <img
                  className={cx('icon', key)}
                  src={THERAPY_TYPE_2_IMAGE[key]?.[selectTherapyType.includes(key) ? 'selected' : 'default']}
                />
                <div>{THERAPY_TYPE_2_TEXT[key]}</div>
              </div>
            ))}
            <When condition={!!selectTherapyType?.length}>
              <div className={cx('time-tips')}>整个流程预计需要{allProcessSpendTime}分钟</div>
            </When>
          </div>
        ) : (
          <div className={cx('user-info')}>
            <div className={cx('user-info-title-item')}>您的性别</div>
            <div className={cx('select-sex')}>
              <div
                className={cx('select-sex-item', { active: userInfo?.gender === 'Female' })}
                onClick={() => setFormValue({ userInfo: { gender: 'Female' } }, { isMerge: true })}
              >
                <Icon name="female" className={cx('icon')} />
                <span>我是女性</span>
              </div>
              <div
                className={cx('select-sex-item', { active: userInfo?.gender === 'Male' })}
                onClick={() => setFormValue({ userInfo: { gender: 'Male' } }, { isMerge: true })}
              >
                <Icon name="male" className={cx('icon')} />
                <span>我是男性</span>
              </div>
            </div>
            <div className={cx('user-info-title-item')}>您的生日</div>
            <div className={cx('birthday-picker')} onClick={() => setIsBirthdaySelectorShow(true)}>
              <div className={cx('birthday-picker-wrapper')}>
                <div>{userInfo?.birthday ? dayjs(userInfo?.birthday).format('YYYY年MM月DD日') : '请选择'}</div>
                <Icon name="arrow-right" />
              </div>
            </div>
          </div>
        )}
      </div>
      <BirthdaySelector
        isShow={isBirthdaySelectorShow}
        setIsShow={setIsBirthdaySelectorShow}
        value={userInfo?.birthday}
        onChange={date => setFormValue({ userInfo: { birthday: date } }, { isMerge: true })}
      />
    </div>
  );
};
